import React, { useState } from 'react';
import { HelpCircle, ChevronDown, ChevronUp, Ticket, Clock, CreditCard, ShieldCheck } from 'lucide-react';

interface FAQItem {
  question: string;
  answer: string;
  icon: React.ReactNode;
}

export const FAQ: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const faqs: FAQItem[] = [
    {
      question: "Bagaimana cara membeli tiket online?",
      answer: "Pilih jenis tiket yang Anda inginkan di halaman depan, tentukan tanggal kunjungan dan jumlah tiket. Setelah itu, isi data diri Anda dengan lengkap dan lakukan pembayaran melalui metode yang tersedia (QRIS/Transfer). E-Tiket akan dikirim otomatis ke email Anda.",
      icon: <Ticket className="w-5 h-5 text-giz-primary" />
    },
    {
      question: "Jam operasional Surabaya Ticket?",
      answer: "Layanan kami tersedia setiap hari mulai pukul 08.00 hingga 16.00 WIB. Untuk wahana GIZ Park dan Rabbits In Wonderland, jam buka menyesuaikan dengan jadwal operasional Kebun Binatang Surabaya.",
      icon: <Clock className="w-5 h-5 text-blue-500" />
    },
    {
      question: "Apakah tiket yang sudah dibeli bisa direfund?",
      answer: "Kebijakan refund bergantung pada jenis tiket yang dibeli. Secara umum, pembatalan yang dilakukan minimal 24 jam sebelum hari kunjungan dapat diproses dengan potongan biaya administrasi. Silakan baca halaman Kebijakan Refund untuk detail lebih lanjut.",
      icon: <CreditCard className="w-5 h-5 text-orange-500" />
    },
    {
      question: "Apakah saya harus mencetak e-tiket?",
      answer: "Tidak perlu. Anda cukup menunjukkan QR Code yang ada di e-tiket melalui smartphone Anda kepada petugas di pintu masuk wahana.",
      icon: <ShieldCheck className="w-5 h-5 text-green-500" />
    },
    {
      question: "Bagaimana jika saya belum menerima e-tiket di email?",
      answer: "Pastikan Anda sudah mengecek folder Spam atau Promotions. Jika masih belum ada, Anda bisa cek status pesanan di halaman 'Cek Pesanan' menggunakan kode booking Anda atau hubungi layanan pelanggan kami via WhatsApp.",
      icon: <HelpCircle className="w-5 h-5 text-indigo-500" />
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 pt-32 pb-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 py-1 px-4 rounded-full bg-white border border-indigo-100 shadow-sm mb-6">
            <span className="w-2 h-2 rounded-full bg-giz-primary animate-pulse"></span>
            <span className="text-giz-primary font-bold text-xs uppercase tracking-wider">Pusat Bantuan</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-4">
            Pertanyaan Sering <span className="text-transparent bg-clip-text bg-gradient-to-r from-giz-primary to-riw-primary">Diajukan</span>
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Temukan jawaban cepat untuk pertanyaan umum mengenai pembelian tiket, kunjungan, dan layanan kami.
          </p>
        </div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className={`bg-white rounded-2xl border transition-all duration-300 ${openIndex === index ? 'border-giz-primary shadow-md' : 'border-gray-200 shadow-sm hover:border-gray-300'}`}
            >
              <button
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                className="w-full px-6 py-5 flex items-center justify-between text-left focus:outline-none"
              >
                <div className="flex items-center gap-4">
                  <div className={`p-2 rounded-lg ${openIndex === index ? 'bg-indigo-50' : 'bg-gray-50'}`}>
                    {faq.icon}
                  </div>
                  <span className={`font-bold text-lg ${openIndex === index ? 'text-gray-900' : 'text-gray-700'}`}>
                    {faq.question}
                  </span>
                </div>
                {openIndex === index ? (
                  <ChevronUp className="w-5 h-5 text-gray-400" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-gray-400" />
                )}
              </button>

              <div className={`overflow-hidden transition-all duration-300 ${openIndex === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
                <div className="px-6 pb-6 pt-2 ml-14 text-gray-600 leading-relaxed border-t border-gray-50 mt-2">
                  {faq.answer}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-16 bg-gradient-to-r from-giz-primary to-riw-primary rounded-3xl p-8 text-white text-center shadow-xl shadow-indigo-200">
          <h3 className="text-2xl font-bold mb-2">Masih punya pertanyaan lainnya?</h3>
          <p className="text-indigo-50 mb-6">Tim dukungan kami siap membantu Anda kapan saja.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="https://wa.me/628123456789"
              className="bg-white text-giz-primary px-8 py-3 rounded-full font-bold hover:bg-opacity-90 transition-all flex items-center justify-center gap-2"
            >
              Hubungi via WhatsApp
            </a>
            <button
              onClick={() => window.scrollTo(0, 0)}
              className="bg-transparent border border-white/30 text-white px-8 py-3 rounded-full font-bold hover:bg-white/10 transition-all"
            >
              Baca Dokumentasi
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
